import React, { cloneElement } from 'react';
import { motion } from 'framer-motion';
import { ThermometerIcon, DropletIcon, WindIcon } from 'lucide-react';
const principles = [{
  icon: <ThermometerIcon className="w-10 h-10 text-[#E69D4F]" data-id="element-165" />,
  title: 'Boundary Layer Control',
  description: <>
        The{' '}
        <span className="text-[#E69D4F] font-semibold" data-id="element-166">
          invisible layer of air
        </span>{' '}
        around your meat determines everything.{' '}
        <span className="text-ivory font-semibold" data-id="element-167">Master it</span>, and you
        control the cook. Ignore it, and your bark will be mush.
      </>
}, {
  icon: <DropletIcon className="w-10 h-10 text-[#EF7D43]" data-id="element-168" />,
  title: 'Interstitial Fat Rendering',
  description: <>
        There's a{' '}
        <span className="text-[#EF7D43] font-semibold" data-id="element-169">12-degree window</span>{' '}
        where fat transforms from chewy to{' '}
        <span className="text-[#F2B851] font-semibold" data-id="element-170">butter</span>. Miss it,
        and{' '}
        <span className="text-red-500 font-semibold" data-id="element-171">no amount of resting</span>{' '}
        saves you.
      </>
}, {
  icon: <WindIcon className="w-10 h-10 text-[#F2B851]" data-id="element-172" />,
  title: 'Heat-Flow Dynamics',
  description: <>
        It's{' '}
        <span className="text-red-500 font-semibold" data-id="element-173">
          not about temperature
        </span>
        . It's about how heat{' '}
        <span className="text-[#F2B851] font-semibold" data-id="element-174">MOVES</span> through your
        grill. This{' '}
        <span className="text-[#E69D4F] font-semibold" data-id="element-175">changes everything</span>{' '}
        about how you set up your coals.
      </>
}];
export function ScienceSection() {
  return <section className="py-16 md:py-20 px-4 bg-[#000000]" data-id="element-176">
      <div className="max-w-5xl mx-auto" data-id="element-177">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-178">
          <h2 className="font-heading text-3xl md:text-5xl font-black text-white mb-4 md:mb-6 uppercase tracking-tight" data-id="element-179">
            The Revelation: Why <span className="text-[#EF7D43]" data-id="element-180">99%</span> of
            Backyard Cooks <span className="text-red-500" data-id="element-181">Fail</span>
          </h2>
          <p className="text-base md:text-xl text-smoke max-w-3xl mx-auto tracking-wide" data-id="element-182">
            It comes down to{' '}
            <span className="text-[#E69D4F] font-semibold" data-id="element-183">
              three scientific principles
            </span>{' '}
            that competition pitmasters have known for decades:
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-184">
          {principles.map((principle, index) => {
          const borderColors = ['border-[#E69D4F]', 'border-[#EF7D43]', 'border-[#F2B851]'];
          const titleColors = ['text-[#E69D4F]', 'text-[#EF7D43]', 'text-[#F2B851]'];
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} className={`bg-dark-elevated rounded-2xl p-6 md:p-10 border-t-4 ${borderColors[index]} relative overflow-hidden group`} data-id="element-185">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity hidden md:block" data-id="element-186">
                  {cloneElement(principle.icon as React.ReactElement, {
                className: 'w-40 h-40 text-amber'
              })}
                </div>

                <div className="relative z-10" data-id="element-187">
                  <div className={`bg-dark w-14 h-14 md:w-20 md:h-20 rounded-full flex items-center justify-center mb-5 md:mb-8 shadow-lg border ${borderColors[index]}/20`} data-id="element-188">
                    {principle.icon}
                  </div>
                  <h3 className={`font-heading text-xl md:text-2xl font-bold ${titleColors[index]} mb-3 md:mb-4 uppercase tracking-tight`} data-id="element-189">
                    {principle.title}
                  </h3>
                  <p className="text-smoke leading-relaxed text-base md:text-lg" data-id="element-190">
                    {principle.description}
                  </p>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}